import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoginLayout from '../../components/layout/LoginLayout';
import Helmet from 'react-helmet';
import { DEFAULT_THEME, type ThemeColors } from '../../constants/themeDefaults';

const PrivacyPolicyPage: React.FC = () => {
  const theme = DEFAULT_THEME;

  return (
    <LoginLayout>
      <Helmet>
        <title>Privacy Policy | MindShare Calendar</title>
        <meta name="description" content="Read MindShare Calendar's privacy policy to understand how we collect, use, and protect your information." />
        <meta name="keywords" content="privacy policy, data protection, MindShare security, user data" />
        <meta property="og:title" content="Privacy Policy | MindShare Calendar" />
        <meta property="og:description" content="Read MindShare Calendar's privacy policy to understand how we collect, use, and protect your information." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mindsharecalendar.com/privacy" />
        <meta property="og:image" content="https://mindsharecalendar.com/og-image.jpg" />
        <link rel="canonical" href="https://mindsharecalendar.com/privacy" />
      </Helmet>

      {/* Header Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: '#F9FAFB',
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h2"
            fontWeight={700}
            align="center"
            sx={{
              mb: 2,
              color: theme.textColor,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              mb: 5,
              maxWidth: 700,
              mx: 'auto',
              color: theme.textColor,
              opacity: 0.8,
            }}
          >
            Last updated: 12th March 2025
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2,
              border: `1px solid ${theme.borderColor}`,
            }}
          >
            <Typography variant="body1" paragraph>
              At MindShare, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our calendar application and related services.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information that you provide directly to us, including:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>Account Information:</strong> When you register, we collect your name, email address, password, and optional profile information.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>Calendar Data:</strong> This includes appointments, events, notes, and any other information you enter into the application.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>Communication Data:</strong> If you contact us directly, we may receive additional information about you, such as your name, email address, the contents of your message, and any attachments you may send.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>Usage Data:</strong> We collect information about how you interact with our service, such as access times, pages viewed, and features used.
                </Typography>
              </li>
            </ul>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1" paragraph>
                  Provide, maintain, and improve our services
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  Process transactions and send related information
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  Send administrative messages, updates, security alerts, and support and administrative messages
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  Respond to your comments, questions, and requests
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  Personalize your experience and deliver content relevant to your interests
                </Typography>
              </li>
            </ul>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              3. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect the security of your personal information. However, please be aware that no security measures are perfect or impenetrable, and we cannot guarantee the security of your data.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              4. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We will retain your information for as long as your account is active or as needed to provide you services. If you wish to cancel your account or request that we no longer use your information, please contact us at privacy@mindsharecalendar.com.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              5. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1" paragraph>
                  The right to access personal information we hold about you
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  The right to request correction or deletion of your personal information
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  The right to object to processing of your personal information
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  The right to data portability
                </Typography>
              </li>
            </ul>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              6. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              7. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Privacy Policy, please contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Email:</strong> privacy@mindsharecalendar.com
            </Typography>
          </Paper>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/terms"
              variant="outlined"
              sx={{
                mx: 1,
                borderColor: theme.navigationColor,
                color: theme.navigationColor,
              }}
            >
              Terms of Service
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              sx={{
                mx: 1,
                borderColor: theme.navigationColor,
                color: theme.navigationColor,
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: alpha(DEFAULT_THEME.navigationColor, 0.03) }}>
        <Container maxWidth="sm">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              bgcolor: DEFAULT_THEME.navigationColor,
              color: '#fff',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: DEFAULT_THEME.secondaryColor,
              }}
            >
              Experience MindShare today
            </Typography>
            <Typography
              variant="body1"
              component="p"
              sx={{
                mb: 3,
                color: alpha(DEFAULT_THEME.secondaryColor, 0.9),
              }}
            >
              Join thousands of professionals managing their schedules effectively with our secure, privacy-focused platform.
            </Typography>

            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                bgcolor: DEFAULT_THEME.backgroundColor,
                color: DEFAULT_THEME.navigationColor,
                '&:hover': {
                  bgcolor: alpha(DEFAULT_THEME.backgroundColor, 0.9),
                },
              }}
            >
              Start your free trial
            </Button>
          </Box>
        </Container>
      </Box>
    </LoginLayout>
  );
};

export default PrivacyPolicyPage; 